import React, { useState } from 'react';
import { Upload, Button, message, Card, Space, Typography, Progress } from 'antd';
import { UploadOutlined, FileSyncOutlined } from '@ant-design/icons';
import { readExcelOrCsv, processAndExportFile } from './fileProcessor';
import type { UploadFile } from 'antd/es/upload/interface';

const { Title, Text } = Typography;

interface PlatformTypeConversionProps {
  username: string;
  isLoggedIn: boolean;
}

/**
 * 自动识别列名映射
 * @param columns 文件列名数组
 * @returns 列名映射配置
 */
function autoDetectColumnMapping(columns: string[]): {
  sourceSite: string | null;
  mediaType: string | null;
  publisherType: string | null;
} {
  const mapping = {
    sourceSite: null as string | null,
    mediaType: null as string | null,
    publisherType: null as string | null,
  };

  // 来源网站的匹配关键词
  const sourceSiteKeywords = ['来源网站', '来源', 'sourceSite', 'source_site', 'source', '来源站点'];
  // 媒体类型的匹配关键词
  const mediaTypeKeywords = ['媒体类型', '媒体', 'mediaType', 'media_type', 'media'];
  // 发布者性质的匹配关键词
  const publisherTypeKeywords = ['发布者性质', '发布者', 'publisherType', 'publisher_type', 'publisher', '发布者类型'];

  // 遍历列名，进行模糊匹配
  columns.forEach(col => {
    const colLower = col.toLowerCase().trim();
    
    // 匹配来源网站
    if (!mapping.sourceSite) {
      for (const keyword of sourceSiteKeywords) {
        if (colLower.includes(keyword.toLowerCase()) || col === keyword) {
          mapping.sourceSite = col;
          break;
        }
      }
    }
    
    // 匹配媒体类型
    if (!mapping.mediaType) {
      for (const keyword of mediaTypeKeywords) {
        if (colLower.includes(keyword.toLowerCase()) || col === keyword) {
          mapping.mediaType = col;
          break;
        }
      }
    }
    
    // 匹配发布者性质
    if (!mapping.publisherType) {
      for (const keyword of publisherTypeKeywords) {
        if (colLower.includes(keyword.toLowerCase()) || col === keyword) {
          mapping.publisherType = col;
          break;
        }
      }
    }
  });

  return mapping;
}

/**
 * 处理单个文件
 */
async function processSingleFile(
  file: File,
  onProgress?: (percent: number) => void,
  onWarning?: (warning: string) => void
): Promise<{ success: boolean; fileName: string; rowCount?: number; error?: string; warnings?: string[] }> {
  try {
    onProgress?.(10);
    
    // 读取文件
    const { columns, rows } = await readExcelOrCsv(file);
    onProgress?.(30);
    
    if (rows.length === 0) {
      return { success: false, fileName: file.name, error: '文件中没有数据' };
    }
    
    // 自动识别列名映射
    const columnMapping = autoDetectColumnMapping(columns);
    onProgress?.(50);
    
    // 检查列名映射，生成警告信息
    const warnings: string[] = [];
    const missingColumns: string[] = [];
    
    if (!columnMapping.sourceSite) {
      missingColumns.push('来源网站');
    }
    if (!columnMapping.mediaType) {
      missingColumns.push('媒体类型');
    }
    if (!columnMapping.publisherType) {
      missingColumns.push('发布者性质');
    }
    
    if (missingColumns.length > 0) {
      const warningMsg = `未找到以下列：${missingColumns.join('、')}。系统将使用默认值"网媒"作为平台类型。`;
      warnings.push(warningMsg);
      onWarning?.(warningMsg);
    }
    
    // 处理数据并导出
    const format = file.name.toLowerCase().endsWith('.csv') ? 'csv' : 'xlsx';
    await processAndExportFile(file, columnMapping, { format });
    onProgress?.(100);
    
    return { 
      success: true, 
      fileName: file.name, 
      rowCount: rows.length,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  } catch (error: any) {
    return { 
      success: false, 
      fileName: file.name, 
      error: error?.message || '处理失败' 
    };
  }
}

const PlatformTypeConversion: React.FC<PlatformTypeConversionProps> = ({ username, isLoggedIn }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [results, setResults] = useState<Record<string, { success: boolean; rowCount?: number; error?: string; warnings?: string[] }>>({});

  // 文件上传前的验证
  const beforeUpload = (file: File) => {
    const isValidType = file.name.toLowerCase().endsWith('.xlsx') || 
                       file.name.toLowerCase().endsWith('.xls') || 
                       file.name.toLowerCase().endsWith('.csv');
    
    if (!isValidType) {
      message.error('只支持上传 .xlsx、.xls 或 .csv 文件');
      return Upload.LIST_IGNORE;
    }
    
    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error('文件大小不能超过 50MB');
      return Upload.LIST_IGNORE;
    }
    
    return false; // 阻止自动上传
  };

  // 处理文件列表变化
  const handleFileChange = (info: any) => {
    setFileList(info.fileList);
    // 清除之前的结果和进度
    if (info.fileList.length === 0) {
      setResults({});
      setProgress({});
    }
  };

  // 批量处理文件
  const handleProcessFiles = async () => {
    if (fileList.length === 0) {
      message.warning('请先上传文件');
      return;
    }

    setProcessing(true);
    setResults({});
    setProgress({});

    const files = fileList
      .filter(item => item.originFileObj)
      .map(item => item.originFileObj!);

    // 逐个处理文件
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileKey = file.name;

      try {
        // 更新进度
        setProgress(prev => ({ ...prev, [fileKey]: 0 }));

        // 处理文件
        const result = await processSingleFile(
          file, 
          (percent) => {
            setProgress(prev => ({ ...prev, [fileKey]: percent }));
          },
          (warning) => {
            message.warning(`${file.name}: ${warning}`);
          }
        );

        // 保存结果
        setResults(prev => ({ ...prev, [fileKey]: result }));

        if (result.success) {
          const warningText = result.warnings && result.warnings.length > 0 
            ? `（注意：${result.warnings.join(' ')}）` 
            : '';
          message.success(`${file.name} 处理成功，共 ${result.rowCount} 行数据${warningText}`);
        } else {
          message.error(`${file.name} 处理失败: ${result.error}`);
        }
      } catch (error: any) {
        setResults(prev => ({ 
          ...prev, 
          [fileKey]: { success: false, error: error?.message || '处理失败' } 
        }));
        message.error(`${file.name} 处理失败: ${error?.message || '未知错误'}`);
      }
    }

    setProcessing(false);
    message.success('所有文件处理完成');
  };

  // 移除文件
  const handleRemove = (file: UploadFile) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    
    // 清除该文件的结果和进度
    if (file.name) {
      const newResults = { ...results };
      const newProgress = { ...progress };
      delete newResults[file.name];
      delete newProgress[file.name];
      setResults(newResults);
      setProgress(newProgress);
    }
  };

  if (!isLoggedIn) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>请先登录</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={4}>
              <FileSyncOutlined style={{ marginRight: 8 }} />
              平台类型转换
            </Title>
            <Text type="secondary">
              上传 Excel 或 CSV 文件，系统将自动识别"来源网站"、"媒体类型"、"发布者性质"列，
              并自动添加"平台类型"列后下载处理后的文件。
            </Text>
          </div>

          <div>
            <Upload
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleFileChange}
              onRemove={handleRemove}
              multiple
              accept=".xlsx,.xls,.csv"
            >
              <Button icon={<UploadOutlined />} disabled={processing}>
                选择文件（支持批量上传）
              </Button>
            </Upload>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                支持格式：.xlsx、.xls、.csv，单个文件不超过 50MB
              </Text>
            </div>
          </div>

          {fileList.length > 0 && (
            <div>
              <Button
                type="primary"
                onClick={handleProcessFiles}
                loading={processing}
                disabled={processing}
                size="large"
              >
                {processing ? '处理中...' : `开始处理 ${fileList.length} 个文件`}
              </Button>
            </div>
          )}

          {/* 显示处理进度和结果 */}
          {Object.keys(progress).length > 0 && (
            <div style={{ marginTop: 24 }}>
              <Title level={5}>处理进度</Title>
              {Object.entries(progress).map(([fileName, percent]) => {
                const result = results[fileName];
                return (
                  <div key={fileName} style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>{fileName}</Text>
                      {result && (
                        <span style={{ marginLeft: 12 }}>
                          {result.success ? (
                            <>
                              <Text type="success">
                                ✓ 成功（{result.rowCount} 行）
                              </Text>
                              {result.warnings && result.warnings.length > 0 && (
                                <Text type="warning" style={{ marginLeft: 8, fontSize: 12 }}>
                                  ⚠ {result.warnings.join(' ')}
                                </Text>
                              )}
                            </>
                          ) : (
                            <Text type="danger">
                              ✗ 失败：{result.error}
                            </Text>
                          )}
                        </span>
                      )}
                    </div>
                    <Progress 
                      percent={percent} 
                      status={result && !result.success ? 'exception' : 'active'}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default PlatformTypeConversion;

